import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@glplus/glpweb-dsl';
import { Panel } from '../components/Panel';
import { runnerApi, RunnerApiError, type Me, type MyApplication, type BankingState } from '../lib/runnerApiClient';
import { buildSteps, doneCount, rightNow, METRO_LABEL, SELF_EDITABLE_STATUSES, APPLICATION_HELP, BANKING_OPEN_STATUSES, INSURANCE_OPEN_STATUSES, showsApplicationHelp, showsReportNote, type Step } from '../lib/onboarding';
import { VerificationCallPanel } from '../components/VerificationCallPanel';
import { EditApplicationDetails } from '../components/EditApplicationDetails';
import { ORANGE, ORANGE_EYEBROW_SX, ORANGE_LINK_STYLE } from '../styles/colors';

/** Same source MainFooter uses — the contact form lives on the public site. */
const PUBLIC_SITE = (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ?? 'https://www.grocerylistplus.com';

const GREEN = '#2E9E3A';
const NAVY = '#343F5B';

/**
 * Deck §3 "The starting line" — the applicant's onboarding tracker. Every
 * state on this page comes from the application row runner-api serves; the
 * Stripe Connect / W-9 buttons are rendered but truthfully disabled until
 * step 5 (Checkr + Stripe integrations) ships.
 */
export default function ApplicationStatus({ me, application, reload }: { me: Me; application: MyApplication | null; reload: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The gate reads LIVE banking state (which reads through to Stripe), the
  // same way the app's tracker does — never a disabled button that pretends
  // the step is not open yet while /banking already works.
  const [banking, setBanking] = useState<BankingState | null>(null);
  const bankingRelevant = Boolean(application) && BANKING_OPEN_STATUSES.includes(application?.status ?? 'submitted');
  useEffect(() => {
    if (!bankingRelevant) return;
    runnerApi.banking().then(setBanking).catch(() => setBanking(null));
  }, [bankingRelevant]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  if (!application) {
    return (
      <Box data-testid="page-application">
        <Kicker />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', mb: 1 }}>{t('application.noneTitle')}</Typography>
        <Typography sx={{ color: '#4b5563' }}>{t('application.noneBody', { email: me.email })}</Typography>
      </Box>
    );
  }

  const steps = buildSteps(application.status, Boolean(me.sub));
  const done = doneCount(steps);
  const now = rightNow(application.status, application.firstName);
  const terminal = ['declined_prescreen', 'declined_checkr', 'withdrawn', 'live'].includes(application.status);
  const canWithdraw = !terminal;

  const withdraw = async () => {
    setBusy(true);
    try {
      await runnerApi.withdrawApplication();
      setFlash({ kind: 'success', msg: t('application.withdrawn') });
      setConfirmOpen(false);
      await reload();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof RunnerApiError ? err.message : t('application.withdrawFailed') });
    } finally {
      setBusy(false);
    }
  };

  const headline =
    application.status === 'live' ? t('application.headlineLive', { name: application.firstName })
    : terminal ? t('application.headlineClosed', { name: application.firstName })
    : t('application.headlineStart', { name: application.firstName });
  const sub = terminal ? now.body : t('application.progress', { done, total: steps.length, first: now.body.split('. ')[0] });

  return (
    <Box data-testid="page-application">
      <Kicker />
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', mb: 0.5, fontSize: { xs: '24px', md: '30px' } }}>{headline}</Typography>
      <Typography sx={{ color: '#4b5563', mb: 3 }}>{sub}</Typography>
      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.7fr 1fr' }, gap: 2.5, alignItems: 'start' }}>
        <Panel title={t('application.yourApplication')} accent={NAVY}>
          {steps.map((s) => <StepRow key={s.key} step={s} />)}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef2f6', display: 'flex', gap: 2, flexWrap: 'wrap', color: '#5f6b7a', fontSize: '13px' }}>
            <span>{METRO_LABEL[application.metro] ?? application.metro}</span>
            <span>· {t(`transport.${application.transportMode}`, { defaultValue: application.transportMode })}</span>
            {application.zonesInterested.length > 0 && <span>{t('application.zones', { list: application.zonesInterested.join(', ') })}</span>}
            <span>{t('application.applied', { date: new Date(application.submittedAt).toLocaleDateString(i18n.language.startsWith('es') ? 'es-US' : 'en-US') })}</span>
          </Box>
        </Panel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Panel title={now.title} accent={terminal && application.status !== 'live' ? '#5f6b7a' : ORANGE}>
            <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>{now.body}</Typography>
            {/*
              🔒 The one route out of a stalled or closed application. Shown on
              the declines AND on the waiting states — a stuck applicant has the
              same question and no answer, and that is where applicants are lost.
              🛑 It never offers report details: those go to Checkr, who hold the
              report. See APPLICATION_HELP in lib/onboarding.
            */}
            {showsApplicationHelp(application.status) && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                <Typography sx={{ color: '#1f2937', fontSize: '14px', fontWeight: 700 }}>
                  {t(APPLICATION_HELP.linkLabel)}
                </Typography>
                <Box
                  component="a"
                  href={`${PUBLIC_SITE}/contact?topic=drive`}
                  sx={{ display: 'inline-block', mt: 0.75, color: ORANGE, fontWeight: 700, fontSize: '14px' }}
                >
                  {t(APPLICATION_HELP.cta)}
                </Box>
                {showsReportNote(application.status) && (
                  <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1 }}>
                    {t(APPLICATION_HELP.reportNote)}
                  </Typography>
                )}
              </Box>
            )}
            {application.status === 'profile_setup' && <CtaLink to="/photo" testId="application-profile-cta" label={t('application.profileCta')} />}
            {application.status === 'insurance_review' && <CtaLink to="/insurance" testId="application-insurance-cta" label={t('application.insuranceCta')} />}
            {application.status === 'training_ack' && (
              // The step is self-service (2026-09-11): the handbook page records
              // the acknowledgement and moves the application on by itself.
              <CtaLink to="/handbook#acknowledge" testId="application-handbook-cta" label={t('application.handbookCta')} />
            )}
            {INSURANCE_OPEN_STATUSES.includes(application.status) && application.status !== 'insurance_review' && (
              <Box sx={{ mt: 1.5 }}>
                <Typography component={Link} to="/insurance" data-testid="application-insurance-link" sx={{ ...ORANGE_LINK_STYLE, textDecoration: 'none' }}>{t('application.insuranceLink')}</Typography>
              </Box>
            )}
          </Panel>
          {application.status === 'verification_call' && <VerificationCallPanel onCompleted={() => void reload()} />}
          {bankingRelevant && !terminal && (
            <Panel title={t('status.gateTitle')} accent={NAVY}>
              <GateRow
                label={t('status.stripe')}
                sub={banking?.payable ? t('status.stripeSub.payable') : banking?.started ? t('status.stripeSub.started') : t('status.stripeSub.none')}
                right={banking?.payable ? <Chip label={t('status.done')} size="small" backgroundColor="#dcfce7" textColor="#15803d" fontWeight={700} fontSize={11} /> : <Button ariaLabel={t('status.stripe')} buttonText={banking?.started ? t('status.continue') : t('status.connect')} variant="contained" size="small" sx={{ backgroundColor: ORANGE }} onClick={() => navigate('/banking')} />}
              />
              <GateRow
                label={t('status.w9')}
                sub={t('status.w9Sub')}
                right={<Chip label={banking?.status?.detailsSubmitted ? t('status.done') : t('status.withStripe')} size="small" backgroundColor={banking?.status?.detailsSubmitted ? '#dcfce7' : '#f1f5f9'} textColor={banking?.status?.detailsSubmitted ? '#15803d' : '#64748b'} fontWeight={700} fontSize={11} />}
              />
              <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1 }}>{t('status.gateNote')}</Typography>
            </Panel>
          )}
          {SELF_EDITABLE_STATUSES.includes(application.status) && (
            <Box>
              <EditApplicationDetails
                application={application}
                onSaved={reload}
                onSuccess={(msg) => setFlash({ kind: 'success', msg })}
              />
            </Box>
          )}
          {canWithdraw && (
            <Box>
              <Button ariaLabel={t('application.withdrawAria')} buttonText={t('application.withdrawButton')} variant="text" size="small" sx={{ color: '#b42318' }} onClick={() => setConfirmOpen(true)} />
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} ariaLabelledBy="withdraw-title" maxWidth="xs" fullWidth>
        <DialogTitle id="withdraw-title">{t('application.withdrawTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>{t('application.withdrawBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button ariaLabel={t('application.keepAria')} buttonText={t('application.keep')} variant="outlined" size="small" disabled={busy} onClick={() => setConfirmOpen(false)} />
          <Button ariaLabel={t('application.confirmAria')} buttonText={busy ? t('application.withdrawing') : t('application.withdraw')} variant="contained" size="small" disabled={busy} sx={{ backgroundColor: '#b42318', '&:hover': { backgroundColor: '#912018' } }} onClick={() => void withdraw()} />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Kicker() {
  const { t } = useTranslation();
  return <Typography sx={{ ...ORANGE_EYEBROW_SX, mb: 0.5 }}>{t('application.kicker')}</Typography>;
}

function StepRow({ step }: { step: Step }) {
  const { t } = useTranslation();
  const dot = step.state === 'done' ? { bg: GREEN, glyph: '✓', fg: '#fff' } : step.state === 'now' ? { bg: ORANGE, glyph: '●', fg: '#fff' } : { bg: '#e5e7eb', glyph: '', fg: '#fff' };
  const chip = step.state === 'done' ? { label: t('status.done'), bg: '#dcfce7', fg: '#15803d' } : step.state === 'now' ? { label: t('application.chipInProgress'), bg: '#ffedd5', fg: '#c2410c' } : step.state === 'blocked' ? { label: '—', bg: '#f1f5f9', fg: '#94a3b8' } : { label: t('application.chipUpcoming'), bg: '#f1f5f9', fg: '#64748b' };
  const muted = step.state === 'upcoming' || step.state === 'blocked';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid #f1f5f9' }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: dot.bg, color: dot.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, flexShrink: 0 }}>{dot.glyph}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: step.state === 'now' ? 800 : 600, color: muted ? '#5f6b7a' : '#1f2937', fontSize: '14px' }}>{step.label}</Typography>
        {step.sub && step.state !== 'done' && <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{step.sub}</Typography>}
      </Box>
      <Chip label={chip.label} size="small" backgroundColor={chip.bg} textColor={chip.fg} fontWeight={700} fontSize={11} />
    </Box>
  );
}

function CtaLink({ to, label, testId }: { to: string; label: string; testId: string }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography component={Link} className="runner-button-link" to={to} data-testid={testId} sx={{ display: 'inline-block', px: 2, py: 1, borderRadius: 2, backgroundColor: ORANGE, color: '#fff', fontWeight: 700, fontSize: '14px', textDecoration: 'none' }}>{label}</Typography>
    </Box>
  );
}

function GateRow({ label, sub, right }: { label: string; sub: string; right: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
      <Box>
        <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '14px' }}>{label}</Typography>
        <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{sub}</Typography>
      </Box>
      {right}
    </Box>
  );
}
